import React, { useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useCloudflareAuth } from '../contexts/CloudflareAuthContext';

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '493789782178-vssd8ee4o7s6rhp014sjhjjo23nianem.apps.googleusercontent.com';

declare global {
  interface Window {
    google?: any;
  }
}

let googleIdentityInitialized = false;
let activeCredentialHandler: ((credential: string) => void) | null = null;

// Google supplies only the verified basic profile: name, email and profile photo.
// New accounts are created immediately as pending for admin approval.
const GoogleLoginButton: React.FC = () => {
  const { googleLogin } = useCloudflareAuth();
  const buttonShellRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const googleLoginRef = useRef(googleLogin);

  useEffect(() => {
    googleLoginRef.current = googleLogin;
  }, [googleLogin]);

  const handleCredential = async (credential: string) => {
    try {
      const result = await googleLoginRef.current(credential);
      if (result === 'ok') {
        toast.success('Signed in with Google');
        window.location.href = '/dashboard';
      } else {
        toast.error('Google login failed');
      }
    } catch (error: any) {
      toast.error(error.message || 'Google login failed');
    }
  };

  useEffect(() => {
    activeCredentialHandler = handleCredential;

    const render = () => {
      if (!window.google?.accounts?.id || !buttonRef.current) return;
      if (!googleIdentityInitialized) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response: any) => activeCredentialHandler?.(response.credential),
        });
        googleIdentityInitialized = true;
      }

      buttonRef.current.innerHTML = '';
      const width = Math.min(
        400,
        Math.max(240, Math.round(buttonShellRef.current?.getBoundingClientRect().width || 320))
      );
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'filled_black',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        logo_alignment: 'left',
        width,
      });
    };

    let resizeObserver: ResizeObserver | undefined;
    const watchButtonWidth = () => {
      if (!buttonShellRef.current || typeof ResizeObserver === 'undefined') return;
      resizeObserver = new ResizeObserver(render);
      resizeObserver.observe(buttonShellRef.current);
    };

    if (window.google?.accounts?.id) {
      render();
      watchButtonWidth();
      return () => resizeObserver?.disconnect();
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://accounts.google.com/gsi/client"]');
    const script = existingScript || document.createElement('script');
    if (!existingScript) {
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', render, { once: true });
    watchButtonWidth();

    return () => {
      resizeObserver?.disconnect();
      script.removeEventListener('load', render);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={buttonShellRef}
      className="relative mx-auto flex min-h-[40px] w-full max-w-[400px] items-center justify-center overflow-visible"
    >
      <div
        ref={buttonRef}
        className="flex w-full items-center justify-center"
        aria-label="Continue with Google"
      />
    </div>
  );
};

// Kept as a default export because both Login and Register share this component.
export default GoogleLoginButton;
